import React, { useState } from 'react';
import {
  Check as ApproveIcon,
  Send as PostIcon,
  Block as VoidIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import LedgerFormDialog from '../LedgerFormDialog';
import { BankAccountPicker, CurrencyPicker } from '../LedgerPickers';
import { useLedgerLookups } from '../Journals';
import { CashStatusChip, fmtDate, getErrorMessage } from './CashShared';

const STATUSES = ['DRAFT', 'APPROVED', 'POSTED', 'VOID'];

const emptyForm = () => ({
  from_account_id: '', to_account_id: '', transfer_date: new Date().toISOString().slice(0, 10),
  currency_id: '', amount: '', reference: '', narrative: '',
});

// Bank-to-bank transfers: DRAFT -> APPROVED -> POSTED (DR destination / CR
// source); VOID reverses the posting journal once posted.
const TransfersTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.CASH, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.LEDGER.CASH, ACTIONS.UPDATE);
  const canApprove = can(RESOURCES.LEDGER.CASH, ACTIONS.APPROVE);
  const look = useLedgerLookups();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [voidDlg, setVoidDlg] = useState({ open: false, item: null, void_reason: '' });

  const reload = () => setRefreshKey((k) => k + 1);

  const openCreate = () => {
    setValues(emptyForm());
    setShowCreate(true);
  };

  const submitCreate = async () => {
    if (!values.from_account_id || !values.to_account_id) {
      showSnackbar('Source and destination accounts are required', 'error');
      return;
    }
    if (values.from_account_id === values.to_account_id) {
      showSnackbar('Source and destination accounts must differ', 'error');
      return;
    }
    if (!values.amount || Number(values.amount) <= 0) {
      showSnackbar('Amount must be greater than zero', 'error');
      return;
    }
    const payload = {
      from_account_id: Number(values.from_account_id),
      to_account_id: Number(values.to_account_id),
      transfer_date: values.transfer_date,
      amount: Number(values.amount),
    };
    if (values.currency_id) payload.currency_id = Number(values.currency_id);
    if (values.reference?.trim()) payload.reference = values.reference.trim();
    if (values.narrative?.trim()) payload.narrative = values.narrative.trim();
    try {
      setSaving(true);
      await ledgerAPI.createBankTransfer(payload);
      showSnackbar('Transfer created as DRAFT', 'success');
      setShowCreate(false);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runWorkflow = (label, pastTense, fn) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} this transfer?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(`Transfer ${pastTense} successfully`, 'success');
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const submitVoid = async () => {
    if ((voidDlg.void_reason?.trim() ?? '').length < 5) {
      showSnackbar('Void reason (min 5 chars) is required', 'error');
      return;
    }
    try {
      await ledgerAPI.voidBankTransfer(voidDlg.item.transfer_id, { void_reason: voidDlg.void_reason.trim() });
      showSnackbar('Transfer voided successfully', 'success');
      setVoidDlg({ open: false, item: null, void_reason: '' });
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.CASH}
        title="Bank Transfers"
        subtitle="Move funds between your own bank/cash accounts"
        addLabel="New Transfer"
        idKey="transfer_id"
        searchPlaceholder="Search transfer #, reference..."
        emptyMessage="No bank transfers found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getBankTransfers(params)}
        onAdd={canCreate ? openCreate : null}
        filters={[{ key: 'status', label: 'Status', options: STATUSES.map((s) => ({ label: s, value: s })) }]}
        columns={[
          { label: 'Transfer #', render: (t) => <strong>{t.transfer_number}</strong> },
          { label: 'From', render: (t) => look.accountLabel(t.from_account_id) },
          { label: 'To', render: (t) => look.accountLabel(t.to_account_id) },
          { label: 'Date', render: (t) => fmtDate(t.transfer_date) },
          { label: 'Amount', render: (t) => formatAccountingAmount(t.amount), align: 'right' },
          { label: 'Status', render: (t) => <CashStatusChip status={t.status} /> },
        ]}
        rowActions={(t) => {
          const actions = [];
          if (t.status === 'DRAFT' && canApprove) {
            actions.push({ icon: ApproveIcon, title: 'Approve', color: 'primary', onClick: () => runWorkflow('Approve', 'approved', () => ledgerAPI.approveBankTransfer(t.transfer_id)) });
          }
          if (t.status === 'APPROVED' && (canUpdate || canApprove)) {
            actions.push({ icon: PostIcon, title: 'Post', color: 'primary', onClick: () => runWorkflow('Post', 'posted', () => ledgerAPI.postBankTransfer(t.transfer_id)) });
          }
          if ((t.status === 'DRAFT' || t.status === 'APPROVED' || t.status === 'POSTED') && canApprove) {
            actions.push({ icon: VoidIcon, title: 'Void', color: 'error', onClick: () => setVoidDlg({ open: true, item: t, void_reason: '' }) });
          }
          return actions;
        }}
      />

      <LedgerFormDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="New Bank Transfer"
        saving={saving}
        onSubmit={submitCreate}
        values={values}
        onChange={(key, value) => setValues((p) => ({ ...p, [key]: value }))}
        fields={[
          { key: 'from_account_id', label: 'From Account', type: 'picker', picker: BankAccountPicker, required: true, md: 6 },
          { key: 'to_account_id', label: 'To Account', type: 'picker', picker: BankAccountPicker, required: true, md: 6 },
          { key: 'transfer_date', label: 'Transfer Date', type: 'date', required: true, md: 6 },
          { key: 'amount', label: 'Amount', type: 'number', required: true, min: 0.01, md: 6 },
          { key: 'currency_id', label: 'Currency (optional)', type: 'picker', picker: CurrencyPicker, md: 6 },
          { key: 'reference', label: 'Reference', md: 6 },
          { key: 'narrative', label: 'Narrative', type: 'textarea', md: 12 },
        ]}
      />

      <LedgerFormDialog
        open={voidDlg.open}
        onClose={() => setVoidDlg({ open: false, item: null, void_reason: '' })}
        title={`Void transfer ${voidDlg.item?.transfer_number || ''}`}
        onSubmit={submitVoid}
        values={voidDlg}
        onChange={(key, value) => setVoidDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'void_reason', label: 'Void Reason (min 5 chars)', type: 'textarea', required: true, md: 12 }]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default TransfersTab;
